import React, { useState } from "react";
import { useGetStockItemsQuery, useGetStockMovementsQuery, useCreateStockItemMutation, useUpdateStockItemMutation, useDeleteStockItemMutation, useCreateStockAdjustmentMutation } from "../services/api";
import { FiAlertTriangle, FiPlus, FiEdit2, FiTrash2, FiTrendingUp, FiTrendingDown } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function StockPage() {
  const { data: stockData, isLoading } = useGetStockItemsQuery();
  const { data: movementsData } = useGetStockMovementsQuery({ limit: 50 });
  const [createStockItem] = useCreateStockItemMutation();
  const [updateStockItem] = useUpdateStockItemMutation();
  const [deleteStockItem] = useDeleteStockItemMutation();
  const [createAdjustment] = useCreateStockAdjustmentMutation();

  const stockItems = stockData?.data || [];
  const movements = movementsData?.data || [];

  // Calculate KPIs
  const totalItems = stockItems.length;
  const totalValue = stockItems.reduce((sum, item) => {
    const qty = Number(item.computed_quantity || item.quantity || 0);
    return sum + (qty * Number(item.unit_cost));
  }, 0);
  const lowStockCount = stockItems.filter(item => {
    const qty = Number(item.computed_quantity || item.quantity || 0);
    return item.low_stock || qty <= Number(item.min_level);
  }).length;
  const totalQuantity = stockItems.reduce((sum, item) => {
    return sum + Number(item.computed_quantity || item.quantity || 0);
  }, 0);

  const [showItemModal, setShowItemModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    name: "",
    material_type: "gold",
    origin: "local",
    unit: "grams",
    min_level: "",
    unit_cost: "",
    notes: "",
  });
  const [quickAddForm, setQuickAddForm] = useState({
    name: "",
    quantity: "",
  });
  const [adjustForm, setAdjustForm] = useState({
    stock_item_id: "",
    quantity: "",
    direction: 1,
    note: "",
  });
  const [error, setError] = useState("");

  const openItemModal = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setItemForm({
        name: item.name,
        material_type: item.material_type,
        origin: item.origin || "local",
        unit: item.unit,
        min_level: item.min_level,
        unit_cost: item.unit_cost,
        notes: item.notes || "",
      });
    } else {
      setEditingId(null);
      setItemForm({
        name: "",
        material_type: "gold",
        origin: "local",
        unit: "grams",
        min_level: "",
        unit_cost: "",
        notes: "",
      });
    }
    setError("");
    setShowItemModal(true);
  };

  const openQuickAddModal = () => {
    setQuickAddForm({
      name: "",
      quantity: "",
    });
    setError("");
    setShowQuickAddModal(true);
  };

  // Smart detection from name
  const detectFromName = (name) => {
    const nameLower = name.toLowerCase();
    
    // Detect material type
    let material_type = "gold"; // default
    if (nameLower.includes("silver")) material_type = "silver";
    else if (nameLower.includes("gemstone") || nameLower.includes("diamond") || nameLower.includes("ruby")) material_type = "gemstone";
    else if (nameLower.includes("gold")) material_type = "gold";
    
    // Detect origin
    let origin = "local"; // default
    if (nameLower.includes("foreign") || nameLower.includes("import")) origin = "foreign";
    else if (nameLower.includes("local")) origin = "local";
    
    return { material_type, origin };
  };

  const openAdjustModal = (item) => {
    setSelectedItem(item);
    setAdjustForm({
      stock_item_id: item.id,
      quantity: "",
      direction: 1,
      note: "",
    });
    setError("");
    setShowAdjustModal(true);
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await updateStockItem({ id: editingId, ...itemForm }).unwrap();
      } else {
        await createStockItem(itemForm).unwrap();
      }
      setShowItemModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to save stock item");
    }
  };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!quickAddForm.name || !quickAddForm.quantity) {
      setError("Please enter name and quantity");
      return;
    }

    try {
      // Detect material type and origin from name
      const detected = detectFromName(quickAddForm.name);
      
      // Create stock item with smart defaults
      const stockPayload = {
        name: quickAddForm.name,
        material_type: detected.material_type,
        origin: detected.origin,
        unit: "grams", // default
        min_level: 100, // default minimum
        unit_cost: 100, // default cost
        notes: "Quick add",
      };
      
      const result = await createStockItem(stockPayload).unwrap();
      
      // Immediately add initial quantity via adjustment
      await createAdjustment({
        stock_item_id: result.data.id,
        quantity: quickAddForm.quantity,
        direction: 1,
        note: "Initial stock (Quick Add)",
      }).unwrap();
      
      setShowQuickAddModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to add stock");
    }
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createAdjustment(adjustForm).unwrap();
      setShowAdjustModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to adjust stock");
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?\n\nThis will permanently remove this stock item and all its movement history.`)) {
      try {
        await deleteStockItem(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || "Failed to delete stock item");
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "220px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Stock Management
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Raw materials inventory tracking
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button 
            onClick={() => openQuickAddModal()} 
            style={{
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
            }}
          >
            <FiPlus /> ⚡ Quick Add
          </button>
          <button 
            onClick={() => openItemModal()}
            style={{
              padding: "0.75rem 1.5rem",
              background: "white",
              color: "#3B82F6",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#3B82F6";
              e.currentTarget.style.background = "#F8FAFC";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E2E8F0";
              e.currentTarget.style.background = "white";
            }}
          >
            <FiPlus /> Advanced
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Items */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              📦
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Items
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalItems}
          </div>
        </div>

        {/* Total Quantity */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ⚖️
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Quantity
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalQuantity.toFixed(2)}g
          </div>
        </div>

        {/* Total Value */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💰
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Value
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(totalValue)}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: lowStockCount > 0 
                ? "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
                : "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              {lowStockCount > 0 ? "⚠️" : "✓"}
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: lowStockCount > 0 ? "#EF4444" : "#10B981",
              fontWeight: 600
            }}>
              {lowStockCount > 0 ? "Attention" : "Healthy"}
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Low Stock
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {lowStockCount}
          </div>
        </div>
      </div>

      {/* Stock Items Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        marginBottom: "2rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          Stock Items
        </h3>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Material</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Type</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Origin</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Quantity</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Min Level</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Unit Cost</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Value</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Status</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {stockItems.map((item) => {
              const qty = Number(item.computed_quantity || item.quantity || 0);
              const value = qty * Number(item.unit_cost);
              const isLow = item.low_stock || qty <= Number(item.min_level);
              return (
                <tr key={item.id} style={{ 
                  borderBottom: "1px solid #F1F5F9",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
                >
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <div style={{ fontWeight: 600, color: "#1E293B", marginBottom: "0.25rem" }}>{item.name}</div>
                    {item.notes && <div style={{ fontSize: "0.8rem", color: "#64748B" }}>{item.notes}</div>}
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: "#FEF3C7",
                      color: "#92400E"
                    }}>
                      {item.material_type}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      background: item.origin === 'local' ? "#D1FAE5" : "#DBEAFE",
                      color: item.origin === 'local' ? "#065F46" : "#1E40AF"
                    }}>
                      {item.origin === 'local' ? '🏠 Local' : '🌍 Foreign'}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>
                    {qty.toFixed(4)} {item.unit}
                  </td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>
                    {Number(item.min_level).toFixed(2)} {item.unit}
                  </td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatCurrency(item.unit_cost)}</td>
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>{formatCurrency(value)}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    {isLow ? (
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: "#FEE2E2",
                        color: "#DC2626",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.25rem"
                      }}>
                        <FiAlertTriangle size={12} /> Low
                      </span>
                    ) : (
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: "#D1FAE5",
                        color: "#065F46"
                      }}>
                        Healthy
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button 
                        onClick={() => openItemModal(item)}
                        style={{
                          padding: "0.5rem 0.75rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#3B82F6",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: "0.35rem",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#F8FAFC";
                          e.currentTarget.style.borderColor = "#3B82F6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiEdit2 size={14} /> Edit
                      </button>
                      <button 
                        onClick={() => openAdjustModal(item)}
                        style={{
                          padding: "0.5rem 0.75rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#8B5CF6",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                          fontWeight: 500,
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#F5F3FF";
                          e.currentTarget.style.borderColor = "#8B5CF6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        Adjust
                      </button>
                      <button 
                        onClick={() => handleDelete(item.id, item.name)}
                        style={{
                          padding: "0.5rem",
                          background: "transparent",
                          border: "1px solid #E2E8F0",
                          borderRadius: "6px",
                          color: "#EF4444",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          transition: "all 0.2s"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#FEF2F2";
                          e.currentTarget.style.borderColor = "#EF4444";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.borderColor = "#E2E8F0";
                        }}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Stock Movements Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#1E293B", marginBottom: "1.25rem" }}>
          Recent Movements
        </h3>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Date</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Stock Item</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Type</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Quantity</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Direction</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Reference</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Note</th>
            </tr>
          </thead>
          <tbody>
            {movements.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: "center", padding: "3rem", color: "#64748B" }}>
                  No movements yet
                </td>
              </tr>
            ) : (
              movements.map((mov) => (
                <tr key={mov.id} style={{ 
                  borderBottom: "1px solid #F1F5F9",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F8FAFC";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                }}
                >
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatDate(mov.created_at)}</td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <div style={{ fontWeight: 600, color: "#1E293B", marginBottom: "0.25rem" }}>{mov.stock_item_name}</div>
                    <div style={{ fontSize: "0.8rem", color: "#64748B" }}>{mov.material_type}</div>
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    <span style={{
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      background: "#F1F5F9",
                      color: "#64748B"
                    }}>
                      {mov.movement_type}
                    </span>
                  </td>
                  <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>
                    {Number(mov.quantity).toFixed(4)} {mov.unit}
                  </td>
                  <td style={{ padding: "1rem 0.75rem" }}>
                    {mov.direction === 1 ? (
                      <span style={{ 
                        color: "#10B981", 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "0.35rem",
                        fontWeight: 600
                      }}>
                        <FiTrendingUp size={16} /> IN
                      </span>
                    ) : (
                      <span style={{ 
                        color: "#EF4444", 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "0.35rem",
                        fontWeight: 600
                      }}>
                        <FiTrendingDown size={16} /> OUT
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{mov.reference_type || "-"}</td>
                  <td style={{ padding: "1rem 0.75rem", fontSize: "0.85rem", color: "#64748B" }}>{mov.note || "-"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Stock Item Modal */}
      {showItemModal && (
        <div className="modal-overlay" onClick={() => setShowItemModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? "Edit Stock Item" : "Add Stock Item"}</h3>
              <button className="btn-ghost" onClick={() => setShowItemModal(false)}>×</button>
            </div>
            <form onSubmit={handleItemSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Material Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={itemForm.name}
                    onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                    required
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Material Type *</label>
                    <select className="form-select" value={itemForm.material_type} onChange={(e) => setItemForm({ ...itemForm, material_type: e.target.value })}>
                      <option value="gold">Gold</option>
                      <option value="silver">Silver</option>
                      <option value="gemstone">Gemstone</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Origin *</label>
                    <select className="form-select" value={itemForm.origin} onChange={(e) => setItemForm({ ...itemForm, origin: e.target.value })}>
                      <option value="local">🏠 Local</option>
                      <option value="foreign">🌍 Foreign</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Unit *</label>
                    <select className="form-select" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}>
                      <option value="grams">Grams (g)</option>
                      <option value="pieces">Pieces (pcs)</option>
                      <option value="kg">Kilograms (kg)</option>
                      <option value="units">Units</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Minimum Level *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={itemForm.min_level}
                      onChange={(e) => setItemForm({ ...itemForm, min_level: e.target.value })}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Unit Cost (ETB) *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={itemForm.unit_cost}
                      onChange={(e) => setItemForm({ ...itemForm, unit_cost: e.target.value })}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <textarea
                      className="form-textarea"
                      value={itemForm.notes}
                      onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })}
                      rows="2"
                    />
                  </div>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowItemModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingId ? "Update" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Stock Modal */}
      {showQuickAddModal && (
        <div className="modal-overlay" onClick={() => setShowQuickAddModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3 className="modal-title">⚡ Quick Add Stock</h3>
              <button className="btn-ghost" onClick={() => setShowQuickAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleQuickAddSubmit}>
              <div className="modal-body">
                <div style={{
                  padding: "var(--space-4)",
                  background: "var(--color-surface-elevated)",
                  borderRadius: "var(--border-radius-sm)",
                  marginBottom: "var(--space-4)",
                  fontSize: "0.875rem",
                  color: "var(--color-text-secondary)",
                }}>
                  <div style={{ fontWeight: 600, marginBottom: "var(--space-2)", color: "var(--color-text)" }}>
                    💡 Smart Detection
                  </div>
                  <div>• Name with "silver" → Silver material</div>
                  <div>• Name with "gold" → Gold material</div>
                  <div>• Name with "local" → Local origin</div>
                  <div>• Name with "foreign" → Foreign origin</div>
                  <div style={{ marginTop: "var(--space-2)", fontStyle: "italic" }}>
                    Example: "Silver Local" → Silver, Local, Grams
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: "1rem", fontWeight: 600 }}>
                    Material Name *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={quickAddForm.name}
                    onChange={(e) => setQuickAddForm({ ...quickAddForm, name: e.target.value })}
                    placeholder="e.g., Silver Local, Gold Foreign, etc."
                    required
                    autoFocus
                    style={{ fontSize: "1.125rem", padding: "12px" }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: "1rem", fontWeight: 600 }}>
                    Initial Quantity (grams) *
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={quickAddForm.quantity}
                    onChange={(e) => setQuickAddForm({ ...quickAddForm, quantity: e.target.value })}
                    placeholder="e.g., 5000"
                    step="0.01"
                    min="0"
                    required
                    style={{ fontSize: "1.125rem", padding: "12px" }}
                  />
                </div>

                <div style={{
                  padding: "var(--space-3)",
                  background: "rgba(255, 215, 0, 0.1)",
                  border: "1px solid var(--color-accent)",
                  borderRadius: "var(--border-radius-sm)",
                  fontSize: "0.875rem",
                }}>
                  <div style={{ fontWeight: 600, marginBottom: "var(--space-1)" }}>Auto-filled defaults:</div>
                  <div>• Unit: Grams</div>
                  <div>• Min Level: 100g</div>
                  <div>• Unit Cost: 100 ETB</div>
                  <div style={{ marginTop: "var(--space-2)", fontSize: "0.8125rem", fontStyle: "italic" }}>
                    You can edit these later if needed
                  </div>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuickAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ fontSize: "1.125rem", padding: "12px 24px" }}>
                  ⚡ Quick Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stock Adjustment Modal */}
      {showAdjustModal && (
        <div className="modal-overlay" onClick={() => setShowAdjustModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Adjust Stock: {selectedItem?.name}</h3>
              <button className="btn-ghost" onClick={() => setShowAdjustModal(false)}>×</button>
            </div>
            <form onSubmit={handleAdjustSubmit}>
              <div className="modal-body">
                <div style={{
                  padding: "var(--space-4)",
                  background: "var(--color-surface-elevated)",
                  borderRadius: "var(--border-radius-sm)",
                  marginBottom: "var(--space-4)",
                }}>
                  <div style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", marginBottom: "var(--space-2)" }}>
                    Current Quantity:
                  </div>
                  <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "var(--color-accent)" }}>
                    {Number(selectedItem?.computed_quantity || selectedItem?.quantity || 0).toFixed(4)} {selectedItem?.unit}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Direction *</label>
                  <select className="form-select" value={adjustForm.direction} onChange={(e) => setAdjustForm({ ...adjustForm, direction: Number(e.target.value) })}>
                    <option value="1">Add (+)</option>
                    <option value="-1">Remove (-)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Quantity *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={adjustForm.quantity}
                    onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                    step="0.0001"
                    min="0.0001"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Reason *</label>
                  <textarea
                    className="form-textarea"
                    value={adjustForm.note}
                    onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })}
                    placeholder="Explain the reason for this adjustment..."
                    rows="3"
                    required
                  />
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAdjustModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Adjust Stock</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
